import { EthSafeTransaction, EthSafeSignature } from '@safe-global/protocol-kit'
import { getAddress, encodeFunctionData } from 'viem'
import type { PublicClient, Abi } from 'viem'
import {
  SIGNATURE_LENGTH_WITH_ADDRESS,
  ADDRESS_HEX_LENGTH,
  SIGNATURE_HEX_LENGTH,
  ZERO_ADDRESS,
  SIMULATION_API_URL,
  HEX_PREFIX_LENGTH,
  TRANSACTIONS_PER_PAGE,
  API_RETRY_MAX_ATTEMPTS,
  API_RETRY_DELAY_MS,
  API_RETRY_BACKOFF_MULTIPLIER,
} from '@/constants/main'
import SafeAbi from '@/abis/Safe.json'

const SAFE_TX_SERVICE_BASE_URLS: Record<number, string> = {
  1: 'https://safe-transaction-mainnet.safe.global',
  11155111: 'https://safe-transaction-sepolia.safe.global',
}

interface SafeTxServiceTx {
  safe: string
  to: string
  value: string
  data: string | null
  operation: number
  gasToken: string | null
  safeTxGas: string
  baseGas: string
  gasPrice: string
  refundReceiver: string | null
  nonce: number
  executionDate: string | null
  submissionDate: string
  modified: string
  blockNumber: number | null
  transactionHash: string | null
  safeTxHash: string
  executor: string | null
  isExecuted: boolean
  isSuccessful: boolean | null
  ethGasPrice: string | null
  gasUsed: string | null
  fee: string | null
  origin: string | null
  dataDecoded: Record<string, unknown> | null
  confirmationsRequired: number
  confirmations: Array<{
    owner: string
    submissionDate: string
    transactionHash: string | null
    signature: string
    signatureType: string
  }>
  signatures: string | null
  trusted: boolean
}

export interface SimulationResult {
  success: boolean
  result?: {
    safeTxGas: string
    baseGas: string
    gasPrice: string
    gasToken: string
    refundReceiver: string
    lastUsedNonce: number
    gasUsed: string
  }
  error?: string
}

export interface ExecutedTransaction {
  tx: EthSafeTransaction
  safeTxHash: string
  executionDate: string | null
  transactionHash: string | null
  isSuccessful: boolean | null
  executor: string | null
  dataDecoded: Record<string, unknown> | null
}

export interface FetchExecutedTransactionsResult {
  success: boolean
  transactions?: ExecutedTransaction[]
  total?: number
  error?: string
}

function getSafeTxServiceUrl(chainId: number): string | null {
  return SAFE_TX_SERVICE_BASE_URLS[chainId] || null
}

function getApiUrl(chainId: number, apiPath: string): string {
  const baseUrl = getSafeTxServiceUrl(chainId)
  if (!baseUrl) {
    throw new Error(
      `Safe Transaction Service not available for chain ${chainId}`
    )
  }

  return `${baseUrl}${apiPath}`
}

function encodeSignatures(safeTx: EthSafeTransaction): string {
  const signatures = safeTx.signatures
    ? Array.from(safeTx.signatures.values())
    : []
  let encodedSignatures = '0x'
  for (const sig of signatures) {
    const signerAddress = sig.signer
      .toLowerCase()
      .slice(HEX_PREFIX_LENGTH)
      .padStart(ADDRESS_HEX_LENGTH, '0')
    const signatureData = sig.data.startsWith('0x')
      ? sig.data.slice(HEX_PREFIX_LENGTH)
      : sig.data
    encodedSignatures +=
      signerAddress + signatureData.padStart(SIGNATURE_HEX_LENGTH, '0')
  }
  return encodedSignatures
}

function getExecTransactionArgs(
  safeTx: EthSafeTransaction,
  encodedSignatures: string
): [
  `0x${string}`,
  bigint,
  `0x${string}`,
  number,
  bigint,
  bigint,
  bigint,
  `0x${string}`,
  `0x${string}`,
  `0x${string}`
] {
  return [
    safeTx.data.to as `0x${string}`,
    BigInt(safeTx.data.value || '0'),
    (safeTx.data.data || '0x') as `0x${string}`,
    safeTx.data.operation || 0,
    BigInt(safeTx.data.safeTxGas || '0'),
    BigInt(safeTx.data.baseGas || '0'),
    BigInt(safeTx.data.gasPrice || '0'),
    (safeTx.data.gasToken || ZERO_ADDRESS) as `0x${string}`,
    (safeTx.data.refundReceiver || ZERO_ADDRESS) as `0x${string}`,
    encodedSignatures as `0x${string}`,
  ]
}

function parseSimulationError(error: unknown): string {
  let errorMessage = 'Transaction simulation failed'
  if (error instanceof Error && error.message) {
    errorMessage = error.message

    if (error.message.includes('GS026')) {
      errorMessage =
        'Transaction cannot be executed. This may be due to insufficient signatures, invalid nonce, or the transaction not being ready for execution.'
    } else if (error.message.includes('GS020')) {
      errorMessage = 'Transaction signatures are invalid or insufficient.'
    } else if (error.message.includes('GS021')) {
      errorMessage = 'Transaction nonce is invalid.'
    } else if (error.message.includes('execution reverted')) {
      const revertMatch = error.message.match(
        /execution reverted(?: \(([^)]+)\))?/
      )
      if (revertMatch && revertMatch[1]) {
        const revertReason = revertMatch[1]
        if (revertReason.includes('GS026')) {
          errorMessage =
            'Transaction cannot be executed. Please ensure the transaction has enough valid signatures and is ready for execution.'
        } else if (revertReason.includes('GS020')) {
          errorMessage = 'Transaction signatures are invalid or insufficient.'
        } else if (revertReason.includes('GS021')) {
          errorMessage =
            'Transaction nonce is invalid. Please check the transaction nonce.'
        } else {
          errorMessage = `Transaction would revert: ${revertReason}`
        }
      } else {
        errorMessage =
          'Transaction would revert. This may be due to insufficient gas, invalid parameters, or contract state.'
      }
    }
  }
  return errorMessage
}

function convertApiTxToEthSafeTransaction(
  apiTx: SafeTxServiceTx
): EthSafeTransaction {
  const signatures = new Map<string, EthSafeSignature>()

  if (apiTx.confirmations && apiTx.confirmations.length > 0) {
    apiTx.confirmations.forEach((confirmation) => {
      if (
        confirmation.signature &&
        confirmation.signature !== '0x' &&
        confirmation.signature.length > HEX_PREFIX_LENGTH
      ) {
        const owner = confirmation.owner.toLowerCase()
        signatures.set(owner, {
          signer: confirmation.owner,
          data: confirmation.signature,
          isContractSignature: false,
          staticPart: () => '0x',
          dynamicPart: () => confirmation.signature,
        } as EthSafeSignature)
      }
    })
  }

  if (
    signatures.size === 0 &&
    apiTx.signatures &&
    apiTx.signatures !== '0x' &&
    apiTx.signatures.length > HEX_PREFIX_LENGTH
  ) {
    const sigString = apiTx.signatures.startsWith('0x')
      ? apiTx.signatures.slice(HEX_PREFIX_LENGTH)
      : apiTx.signatures

    // Parse signatures: each signature is 170 hex chars (40 for address + 130 for signature)
    const signatureLength = SIGNATURE_LENGTH_WITH_ADDRESS
    let offset = 0
    while (offset + signatureLength <= sigString.length) {
      const addressHex = sigString.slice(offset, offset + ADDRESS_HEX_LENGTH)
      const signatureHex = sigString.slice(
        offset + ADDRESS_HEX_LENGTH,
        offset + signatureLength
      )
      const address = `0x${addressHex}`
      const signature = `0x${signatureHex}`

      signatures.set(address.toLowerCase(), {
        signer: address,
        data: signature,
        isContractSignature: false,
        staticPart: () => '0x',
        dynamicPart: () => signature,
      } as EthSafeSignature)

      offset += signatureLength
    }
  }

  const txData = {
    to: apiTx.to,
    value: apiTx.value || '0',
    data: apiTx.data || '0x',
    operation: apiTx.operation || 0,
    safeTxGas: apiTx.safeTxGas || '0',
    baseGas: apiTx.baseGas || '0',
    gasPrice: apiTx.gasPrice || '0',
    gasToken: apiTx.gasToken || ZERO_ADDRESS,
    refundReceiver: apiTx.refundReceiver || ZERO_ADDRESS,
    nonce: apiTx.nonce,
  }

  const safeTx = new EthSafeTransaction(txData)

  signatures.forEach((sig) => {
    safeTx.addSignature(sig)
  })

  return safeTx
}

export async function submitSafeTransactionToService(
  safeTx: EthSafeTransaction,
  safeAddress: string,
  chainId: number,
  senderAddress: string,
  getSafeTransactionHash: (safeTx: EthSafeTransaction) => Promise<string>
): Promise<{ success: boolean; error?: string }> {
  try {
    const txServiceUrl = getSafeTxServiceUrl(chainId)
    if (!txServiceUrl) {
      return {
        success: false,
        error: `Safe Transaction Service not available for chain ${chainId}`,
      }
    }

    const safeTxHash = await getSafeTransactionHash(safeTx)

    const signatures = safeTx.signatures
      ? Array.from(safeTx.signatures.values())
      : []

    if (signatures.length === 0) {
      return {
        success: false,
        error: 'Transaction has no signatures',
      }
    }

    const senderSignature = signatures.find(
      (sig) => sig.signer.toLowerCase() === senderAddress.toLowerCase()
    )

    if (!senderSignature) {
      return {
        success: false,
        error: 'Sender signature not found in transaction',
      }
    }

    const senderSignatureBytes = senderSignature.data

    const txData = safeTx.data

    const payload = {
      to: txData.to,
      value: txData.value || '0',
      data: txData.data || '0x',
      operation: txData.operation || 0,
      safeTxGas: txData.safeTxGas || '0',
      baseGas: txData.baseGas || '0',
      gasPrice: txData.gasPrice || '0',
      gasToken: txData.gasToken || ZERO_ADDRESS,
      refundReceiver: txData.refundReceiver || ZERO_ADDRESS,
      nonce: String(txData.nonce),
      contractTransactionHash: safeTxHash,
      sender: getAddress(senderAddress),
      signature: senderSignatureBytes,
      origin: 'Dashboard',
    }

    const checksummedSafeAddress = getAddress(safeAddress)
    const url = `${txServiceUrl}/api/v1/safes/${checksummedSafeAddress}/multisig-transactions/`

    const apiPath = url.replace(txServiceUrl, '')
    const apiUrl = getApiUrl(chainId, apiPath)

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorDetails = errorText
        try {
          const errorJson = JSON.parse(errorText)
          errorDetails = JSON.stringify(errorJson, null, 2)
        } catch {
          // If not JSON, use as is
        }
        console.error('Safe Transaction Service error:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorDetails,
          payload: JSON.stringify(payload, null, 2),
        })
        return {
          success: false,
          error: `Failed to submit transaction: ${response.status} ${response.statusText}. ${errorDetails}`,
        }
      }

      return { success: true }
    } catch (fetchError: unknown) {
      console.error(
        'Error submitting transaction to Safe Transaction Service:',
        {
          error: fetchError,
          message: fetchError instanceof Error ? fetchError.message : undefined,
        }
      )

      return {
        success: false,
        error:
          fetchError instanceof Error
            ? fetchError.message
            : 'Unknown error occurred',
      }
    }
  } catch (error) {
    console.error(
      'Error submitting transaction to Safe Transaction Service:',
      error
    )
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

export async function getSafeNonceFromService(
  safeAddress: string,
  chainId: number
): Promise<{ success: boolean; nonce?: number; error?: string }> {
  try {
    const txServiceUrl = getSafeTxServiceUrl(chainId)
    if (!txServiceUrl) {
      return {
        success: false,
        error: `Safe Transaction Service not available for chain ${chainId}`,
      }
    }

    const checksummedSafeAddress = getAddress(safeAddress)
    const apiPath = `/api/v1/safes/${checksummedSafeAddress}/`
    const apiUrl = getApiUrl(chainId, apiPath)

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Safe Transaction Service error:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      })
      return {
        success: false,
        error: `Failed to fetch safe info: ${response.status} ${response.statusText}. ${errorText}`,
      }
    }

    const data = await response.json()
    const nonce = data.nonce !== undefined ? Number(data.nonce) : undefined

    return {
      success: true,
      nonce,
    }
  } catch (error) {
    console.error('Error fetching safe nonce from service:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

export async function fetchPendingTransactionsFromService(
  safeAddress: string,
  chainId: number,
  currentSafeNonce?: number
): Promise<{
  success: boolean
  transactions?: EthSafeTransaction[]
  transactionsWithMeta?: Array<{
    tx: EthSafeTransaction
    safeTxHash: string
    confirmationsRequired: number
    confirmationsCount: number
    dataDecoded: Record<string, unknown> | null
    submissionDate: string
  }>
  error?: string
}> {
  try {
    const txServiceUrl = getSafeTxServiceUrl(chainId)
    if (!txServiceUrl) {
      return {
        success: false,
        error: `Safe Transaction Service not available for chain ${chainId}`,
      }
    }

    const checksummedSafeAddress = getAddress(safeAddress)
    const apiPath = `/api/v1/safes/${checksummedSafeAddress}/multisig-transactions/?executed=false&queued=true`
    const apiUrl = getApiUrl(chainId, apiPath)

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Safe Transaction Service error:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      })
      return {
        success: false,
        error: `Failed to fetch transactions: ${response.status} ${response.statusText}. ${errorText}`,
      }
    }

    const data = await response.json()

    const apiTransactions: SafeTxServiceTx[] = data.results || []

    let filteredTransactions = apiTransactions.filter((tx) => !tx.isExecuted)

    // If currentSafeNonce is provided, filter out transactions with nonce < currentSafeNonce
    // This handles the case where a transaction with the same nonce was already executed
    // (Safe nonce increments after execution, so pending transactions with old nonce are invalid)
    if (currentSafeNonce !== undefined) {
      filteredTransactions = filteredTransactions.filter(
        (tx) => tx.nonce >= currentSafeNonce
      )
    }

    const pendingTransactions = filteredTransactions.map((tx) =>
      convertApiTxToEthSafeTransaction(tx)
    )

    const transactionsWithMeta = filteredTransactions.map((apiTx) => ({
      tx: convertApiTxToEthSafeTransaction(apiTx),
      safeTxHash: apiTx.safeTxHash,
      confirmationsRequired: apiTx.confirmationsRequired,
      confirmationsCount: apiTx.confirmations.length,
      dataDecoded: apiTx.dataDecoded,
      submissionDate: apiTx.submissionDate,
    }))

    return {
      success: true,
      transactions: pendingTransactions,
      transactionsWithMeta,
    }
  } catch (error) {
    console.error(
      'Error fetching transactions from Safe Transaction Service:',
      error
    )
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

export async function fetchExecutedTransactionsFromService(
  safeAddress: string,
  chainId: number,
  limit: number = TRANSACTIONS_PER_PAGE,
  offset: number = 0,
  filters?: {
    executionDateFrom?: string
    executionDateTo?: string
    nonce?: string
  }
): Promise<FetchExecutedTransactionsResult> {
  const txServiceUrl = getSafeTxServiceUrl(chainId)
  if (!txServiceUrl) {
    return {
      success: false,
      error: `Safe Transaction Service not available for chain ${chainId}`,
    }
  }

  const checksummedSafeAddress = getAddress(safeAddress)
  const queryParams = new URLSearchParams({
    executed: 'true',
    limit: limit.toString(),
    offset: offset.toString(),
    ordering: '-execution_date',
  })

  if (filters) {
    if (filters.executionDateFrom) {
      queryParams.append('execution_date__gte', filters.executionDateFrom)
    }
    if (filters.executionDateTo) {
      queryParams.append('execution_date__lte', filters.executionDateTo)
    }
    if (filters.nonce) {
      queryParams.append('nonce', filters.nonce)
    }
  }

  const apiPath = `/api/v1/safes/${checksummedSafeAddress}/multisig-transactions/?${queryParams.toString()}`
  const apiUrl = getApiUrl(chainId, apiPath)

  let lastError: FetchExecutedTransactionsResult | null = null
  for (let attempt = 0; attempt < API_RETRY_MAX_ATTEMPTS; attempt++) {
    try {
      if (attempt > 0) {
        const delay =
          API_RETRY_DELAY_MS *
          Math.pow(API_RETRY_BACKOFF_MULTIPLIER, attempt - 1)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 429) {
        const errorText = await response.text()
        console.warn(
          `Rate limited (429) on attempt ${
            attempt + 1
          }/${API_RETRY_MAX_ATTEMPTS}. Retrying...`,
          { errorText }
        )
        lastError = {
          success: false,
          error: `Rate limited: ${response.statusText}. ${errorText}`,
        }
        continue
      }

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Safe Transaction Service error:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
        })
        return {
          success: false,
          error: `Failed to fetch transactions: ${response.status} ${response.statusText}. ${errorText}`,
        }
      }

      const data = await response.json()

      const apiTransactions: SafeTxServiceTx[] = data.results || []
      const total = data.count !== undefined ? Number(data.count) : undefined

      const executedTransactions = apiTransactions
        .filter((tx) => tx.isExecuted)
        .map((apiTx) => ({
          tx: convertApiTxToEthSafeTransaction(apiTx),
          safeTxHash: apiTx.safeTxHash,
          executionDate: apiTx.executionDate,
          transactionHash: apiTx.transactionHash,
          isSuccessful: apiTx.isSuccessful,
          executor: apiTx.executor,
          dataDecoded: apiTx.dataDecoded,
        }))

      return {
        success: true,
        transactions: executedTransactions,
        total,
      }
    } catch (error) {
      console.error(
        `Error fetching executed transactions (attempt ${
          attempt + 1
        }/${API_RETRY_MAX_ATTEMPTS}):`,
        error
      )
      lastError = {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      }
      if (attempt < API_RETRY_MAX_ATTEMPTS - 1) {
        continue
      }
    }
  }

  return (
    lastError || {
      success: false,
      error: 'Failed to fetch transactions after all retry attempts',
    }
  )
}

export async function rejectTransactionFromService(
  safeTxHash: string,
  safeAddress: string,
  chainId: number,
  signRejection: (safeTxHash: string) => Promise<string>
): Promise<{ success: boolean; error?: string }> {
  try {
    const txServiceUrl = getSafeTxServiceUrl(chainId)
    if (!txServiceUrl) {
      return {
        success: false,
        error: `Safe Transaction Service not available for chain ${chainId}`,
      }
    }

    const signature = await signRejection(safeTxHash)

    const checksummedSafeAddress = getAddress(safeAddress)
    const apiPath = `/api/v1/safes/${checksummedSafeAddress}/multisig-transactions/${safeTxHash}/`
    const apiUrl = getApiUrl(chainId, apiPath)

    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        signature,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Safe Transaction Service error:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      })
      return {
        success: false,
        error: `Failed to reject transaction: ${response.status} ${response.statusText}. ${errorText}`,
      }
    }

    return { success: true }
  } catch (error) {
    console.error(
      'Error rejecting transaction from Safe Transaction Service:',
      error
    )
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

export async function simulateTransaction(
  safeTx: EthSafeTransaction,
  safeAddress: string,
  chainId: number,
  publicClient: PublicClient,
  safeTxHash?: string
): Promise<SimulationResult> {
  try {
    if (safeTxHash) {
      try {
        const encodedSignatures = encodeSignatures(safeTx)
        const execTransactionArgs = getExecTransactionArgs(
          safeTx,
          encodedSignatures
        )

        const execTransactionData = encodeFunctionData({
          abi: SafeAbi as Abi,
          functionName: 'execTransaction',
          args: execTransactionArgs,
        })

        const signatures = safeTx.signatures
          ? Array.from(safeTx.signatures.values())
          : []
        const executorAddress =
          signatures.length > 0 ? signatures[0].signer : safeAddress

        const gasLimit =
          BigInt(safeTx.data.safeTxGas || '0') +
          BigInt(safeTx.data.baseGas || '0') +
          BigInt(50000) // overhead

        const simulationPayload = {
          to: getAddress(safeAddress),
          from: getAddress(executorAddress),
          gas: Number(gasLimit),
          gas_price: safeTx.data.gasPrice || '0',
          input: execTransactionData,
          network_id: String(chainId),
          save: true,
          save_if_fails: true,
        }

        const response = await fetch(`${SIMULATION_API_URL}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(simulationPayload),
        })

        if (response.ok) {
          const data = await response.json()
          return {
            success: true,
            result: data,
          }
        } else {
          const errorText = await response.text()
          let errorDetails = errorText
          try {
            const errorJson = JSON.parse(errorText)
            errorDetails = errorJson.detail || errorJson.message || errorText
          } catch {
            console.error('Error parsing error text:', errorText)
          }
          console.warn(
            'Safe Simulation API error, falling back to local simulation:',
            errorDetails
          )
        }
      } catch (apiError) {
        console.warn(
          'Safe Simulation API not available, using local simulation'
        )
      }
    }

    if (!publicClient) {
      return {
        success: false,
        error: 'Public client not available for local simulation',
      }
    }

    const encodedSignatures = encodeSignatures(safeTx)

    let targetGasEstimate: bigint | null = null
    try {
      if (safeTx.data.to && safeTx.data.data && safeTx.data.data !== '0x') {
        targetGasEstimate = await publicClient.estimateGas({
          to: safeTx.data.to as `0x${string}`,
          data: safeTx.data.data as `0x${string}`,
          value: BigInt(safeTx.data.value || '0'),
        })
      }
    } catch (targetError) {
      console.error('Error estimating gas for target contract:', targetError)
    }

    try {
      const execTransactionArgs = getExecTransactionArgs(
        safeTx,
        encodedSignatures
      )

      await publicClient.simulateContract({
        address: safeAddress as `0x${string}`,
        abi: SafeAbi as Abi,
        functionName: 'execTransaction',
        args: execTransactionArgs,
        value: BigInt(safeTx.data.value || '0'),
      })

      let execGasEstimate: bigint | null = null
      try {
        const estimateData = encodeFunctionData({
          abi: SafeAbi as Abi,
          functionName: 'execTransaction',
          args: execTransactionArgs,
        })
        execGasEstimate = await publicClient.estimateGas({
          to: safeAddress as `0x${string}`,
          data: estimateData,
          value: BigInt(safeTx.data.value || '0'),
        })
      } catch (gasError) {
        console.error('Error estimating gas for execTransaction:', gasError)
      }

      return {
        success: true,
        result: {
          safeTxGas: safeTx.data.safeTxGas || '0',
          baseGas: safeTx.data.baseGas || '0',
          gasPrice: safeTx.data.gasPrice || '0',
          gasToken:
            safeTx.data.gasToken ||
            '0x0000000000000000000000000000000000000000',
          refundReceiver:
            safeTx.data.refundReceiver ||
            '0x0000000000000000000000000000000000000000',
          lastUsedNonce: safeTx.data.nonce,
          gasUsed: execGasEstimate
            ? execGasEstimate.toString()
            : targetGasEstimate
            ? targetGasEstimate.toString()
            : '0',
        },
      }
    } catch (simError: unknown) {
      const errorMessage = parseSimulationError(simError)

      return {
        success: false,
        error: errorMessage,
        result: targetGasEstimate
          ? {
              safeTxGas: safeTx.data.safeTxGas || '0',
              baseGas: safeTx.data.baseGas || '0',
              gasPrice: safeTx.data.gasPrice || '0',
              gasToken:
                safeTx.data.gasToken ||
                '0x0000000000000000000000000000000000000000',
              refundReceiver:
                safeTx.data.refundReceiver ||
                '0x0000000000000000000000000000000000000000',
              lastUsedNonce: safeTx.data.nonce,
              gasUsed: targetGasEstimate.toString(),
            }
          : undefined,
      }
    }
  } catch (error) {
    console.error('Error simulating transaction:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}
